import {createApp, h} from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import ToastService from "primevue/toastservice";
import PrimeVue from "primevue/config";
import "primevue/resources/primevue.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import {createI18n} from "vue-i18n";
import ConfirmDialog from "primevue/confirmdialog";
import ConfirmationService from "primevue/confirmationservice";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import router from "./router";
import store from "./store";
import App from "./App";
import {setDomain, isTokenValid, logout} from "./swagger/vue-api-client";
import "./assets/theme/mytheme/theme.scss";
import {SystemRole} from "./utils/SystemRole";
import {messagesEN} from "./messages/messages_en";
import "./validators";

axios.defaults.withCredentials = true;
setDomain(process.env.VUE_APP_API_URL);

router.beforeEach((to, from, next) => {
    if (to.matched.some((record) => record.meta.requiresAuth)) {
        isTokenValid().then(() => {
            const role = SystemRole.getUserRole();
            if (to.matched.some((record) => record.meta.roles && record.meta.roles.indexOf(role) < 0)) {
                next({ name: "forbidden" });
            } else {
                next();
            }
        }, () => {
            localStorage.removeItem("token");
            next({ name: "login", query: { next: to.fullPath } });
        });
    } else {
        next();
    }
});

const messages = {
    EN: {
        message: messagesEN,
    },
};

const i18n = createI18n({
    locale: localStorage.getItem("language") ? localStorage.getItem("language") : "EN",
    messages,
});

const app = createApp({
    i18n,
    render: () => h(App),
    }).use(router)
    .use(store)
    .use(VueAxios, axios)
    .use(ToastService)
    .use(i18n)
    .use(PrimeVue)
    .use(ConfirmationService)
    .use(ConfirmDialog);


app.axios.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    if (token) {
        config.headers.Authorization = "Bearer " + token;
    }

    console.log(" header ", config.headers["X-Frontend-Breed-Source"])
    if (!config.headers["X-Frontend-Breed-Source"]) {
        const horseBreed = localStorage.getItem("horseBreed");
        if (horseBreed) {
            config.headers["X-Frontend-Breed-Source"] = horseBreed;
        }
    }

    return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

app.axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const toast = app.config.globalProperties.$toast;

        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            window.console.log("originalRequest.url" + originalRequest.url);
            //In order to avoid loop of redirects
            if (originalRequest.url.includes('/refresh-token') || originalRequest.url.includes('/logout')) {
                localStorage.removeItem("token");
                router.push({ name: "login" });
                return Promise.reject(error);
            } else if (originalRequest.url.includes('/login')) {
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise(function (resolve, reject) {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        originalRequest.headers["Authorization"] = "Bearer " + token;
                        return axios(originalRequest);
                    })
                    .catch((err) => {
                        return Promise.reject(err);
                    });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const rs = await axios.post(
                    process.env.VUE_APP_API_URL + "/refresh-token",
                    {},
                    { withCredentials: true }
                );

                const newToken = rs.data.accessToken || rs.data.token;

                localStorage.setItem("token", newToken);

                app.axios.defaults.headers.common["Authorization"] = "Bearer " + newToken;
                originalRequest.headers["Authorization"] = "Bearer " + newToken;

                processQueue(null, newToken);

                return axios(originalRequest);

            } catch (refreshError) {
                processQueue(refreshError, null);

                localStorage.removeItem("token");
                logout();
                router.push({ name: "login" });

                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        if (error.response && error.response.status === 403) {
            toast.add({
                severity: "error",
                summary: "Error",
                detail: "Forbidden - Not Authorized",
                life: 3000,
            });
        }

        return Promise.reject(error);
    }
);

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    zoomPlugin,
);

app.mount("#app");
